import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  GENDER,
  USER_PROFILE_DATA,
  PROVIDER_ACCESS_OPTIONS,
} from "../../facility-management-screen/constants";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import Input from "../../../component-lib/Atoms/Input/Input";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import ContactInput from "../../../component-lib/Atoms/Input/ContactInput";
import { normalizeContactNumber } from "../../../utils/phoneNumberFormatter";
import { roles } from "../constants";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";

interface AdminUserProfileDialogProps {
  isOpen: boolean;
  handleData: (data: any) => void;
  handleClose: () => void;
  selectedUserTask?: any;
  user?: string;
}

export default function AdminUserProfileDialog({
  isOpen = false,
  selectedUserTask,
  handleClose,
  handleData,
  user,
}: AdminUserProfileDialogProps) {
  const dialogHeading = selectedUserTask?.id ? `Edit ${user}` : `Add ${user}`;
  const validationSchema = Yup.object().shape({
    [USER_PROFILE_DATA.BSA_ID]: Yup.number()
      // .matches(VALIDATION_CONSTANTS.NUMERIC, "BSA ID must contain only numbers")
      .required("BSA ID is required"),
    [USER_PROFILE_DATA?.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "First name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("First name is required"),
    [USER_PROFILE_DATA?.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Last name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("Last name is required"),
    [USER_PROFILE_DATA?.ROLE]: Yup.string().required("Role is required"),
    [USER_PROFILE_DATA?.GENDER]: Yup.string().required("Gender is required"),
    // [USER_PROFILE_DATA?.PROVIDER_ACCESS]: Yup.string().required("Provider Access is required"),
    // [USER_PROFILE_DATA?.CONTACT_NUMBER]: Yup.string()
    //   .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
    //   .required("Contact number is required"),
    [USER_PROFILE_DATA.EMAIL]: Yup.string()
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [USER_PROFILE_DATA?.BSA_ID]: `${selectedUserTask?.id || ""}`,
          [USER_PROFILE_DATA?.FIRST_NAME]: `${
            selectedUserTask?.userName?.split(" ")[0] || ""
          }`,
          [USER_PROFILE_DATA?.LAST_NAME]: `${
            selectedUserTask?.userName?.split(" ")[1] || ""
          }`,
          [USER_PROFILE_DATA.ROLE]: `${selectedUserTask?.originalRole || selectedUserTask?.role || ""}`,
          [USER_PROFILE_DATA?.EMAIL]: `${selectedUserTask?.email || ""}`,
          [USER_PROFILE_DATA?.GENDER]: `${selectedUserTask?.originalGender || selectedUserTask?.gender || ""}`,
          [USER_PROFILE_DATA?.PROVIDER_ACCESS]: `${selectedUserTask?.originalProviderAccess || selectedUserTask?.providerAccess || ""}`,
          [USER_PROFILE_DATA?.CONTACT_NUMBER]: normalizeContactNumber(
            selectedUserTask?.contactNumber
          ),
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                {/* <div className="pt-4">
                    <EventEHRUploadImageCard
                      imageUrl={null}
                      handleImage={(data) => {}}
                    />
                  </div> */}

                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      disabled={selectedUserTask?.id ? true : false}
                      type="number"
                      textFieldLabel="BSA ID"
                      // isStarPresent={true}
                      name={USER_PROFILE_DATA.BSA_ID}
                      value={values[USER_PROFILE_DATA.BSA_ID]}
                      onChangeCb={(e) => {
                        setFieldValue(USER_PROFILE_DATA.BSA_ID, e.target.value);
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[USER_PROFILE_DATA.BSA_ID] &&
                        Boolean(errors[USER_PROFILE_DATA.BSA_ID])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.BSA_ID]
                          ? errors[USER_PROFILE_DATA.BSA_ID]
                          : undefined
                      }
                    />
                  </div>

                  <div className="pt-4">
                    <Input
                      textFieldLabel="First Name"
                      // placeholder=""
                      size="small"
                      maxLength={40}
                      name={USER_PROFILE_DATA.FIRST_NAME}
                      value={values[USER_PROFILE_DATA.FIRST_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          USER_PROFILE_DATA.FIRST_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[USER_PROFILE_DATA.FIRST_NAME] &&
                        Boolean(errors[USER_PROFILE_DATA.FIRST_NAME])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.FIRST_NAME]
                          ? errors[USER_PROFILE_DATA.FIRST_NAME]
                          : undefined
                      }
                    />
                  </div>

                  <Input
                    textFieldLabel="Last Name"
                    // placeholder=""
                    size="small"
                    maxLength={40}
                    name={USER_PROFILE_DATA.LAST_NAME}
                    value={values[USER_PROFILE_DATA.LAST_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        USER_PROFILE_DATA.LAST_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.LAST_NAME] &&
                      Boolean(errors[USER_PROFILE_DATA.LAST_NAME])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.LAST_NAME]
                        ? errors[USER_PROFILE_DATA.LAST_NAME]
                        : undefined
                    }
                  />

                  {/* PROVIDER TYPE DROPDOWN */}
                  <Input
                    textFieldLabel="Email"
                    name={USER_PROFILE_DATA.EMAIL}
                    value={values[USER_PROFILE_DATA.EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(USER_PROFILE_DATA.EMAIL, e.target.value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[USER_PROFILE_DATA.EMAIL] &&
                      Boolean(errors[USER_PROFILE_DATA.EMAIL])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.EMAIL]
                        ? errors[USER_PROFILE_DATA.EMAIL]
                        : undefined
                    }
                  />
                  {/* SPECIALITY DROPDOWN */}
                  <SelectDropdown
                    label="Role"
                    // isStarPresent={true}
                    value={values[USER_PROFILE_DATA.ROLE]}
                    options={roles}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.ROLE, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.ROLE] &&
                      Boolean(errors[USER_PROFILE_DATA.ROLE])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.ROLE]
                        ? errors[USER_PROFILE_DATA.ROLE]
                        : undefined
                    }
                  />

                  {/* GENDER DROPDOWN */}
                  <SelectDropdown
                    label="Gender"
                    // isStarPresent={true}
                    value={values[USER_PROFILE_DATA.GENDER]}
                    options={GENDER}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.GENDER, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.GENDER] &&
                      Boolean(errors[USER_PROFILE_DATA.GENDER])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.GENDER]
                        ? errors[USER_PROFILE_DATA.GENDER]
                        : undefined
                    }
                  />
                  {/* <Input
                    textFieldLabel="Work Location"
                    // placeholder=""
                    size="small"
                    name={USER_PROFILE_DATA.WORK_LOCATION}
                    value={values[USER_PROFILE_DATA.WORK_LOCATION]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        USER_PROFILE_DATA.WORK_LOCATION,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.WORK_LOCATION] &&
                      Boolean(errors[USER_PROFILE_DATA.WORK_LOCATION])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.WORK_LOCATION]
                        ? errors[USER_PROFILE_DATA.WORK_LOCATION]
                        : undefined
                    }
                  /> */}

                  {/* <SelectDropdown
                      label="Status"
                      // isStarPresent={true}
                      value={values[USER_PROFILE_DATA.STATUS]}
                      options={GENDER}
                      onChangeCb={(e) =>
                        setFieldValue(USER_PROFILE_DATA.STATUS, e.target.value)
                      }
                      error={
                        touched[USER_PROFILE_DATA.STATUS] &&
                        Boolean(errors[USER_PROFILE_DATA.STATUS])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.STATUS]
                          ? errors[USER_PROFILE_DATA.STATUS]
                          : undefined
                      }
                    /> */}

                  {/* CONTACT INPUT TEXTFIELD */}
                  <ContactInput
                    textFieldLabel="Contact Number"
                    name={USER_PROFILE_DATA.CONTACT_NUMBER}
                    value={values[USER_PROFILE_DATA.CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(USER_PROFILE_DATA.CONTACT_NUMBER, value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[USER_PROFILE_DATA.CONTACT_NUMBER] &&
                      Boolean(errors[USER_PROFILE_DATA.CONTACT_NUMBER])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.CONTACT_NUMBER]
                        ? errors[USER_PROFILE_DATA.CONTACT_NUMBER]
                        : undefined
                    }
                    isContactNumber={true}
                  />

                   {/* PROVIDER ACCESS DROPDOWN */}
                   <SelectDropdown
                    label="Provider Access"
                    // isStarPresent={true}
                    value={values[USER_PROFILE_DATA.PROVIDER_ACCESS]}
                    options={PROVIDER_ACCESS_OPTIONS}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.PROVIDER_ACCESS, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.PROVIDER_ACCESS] &&
                      Boolean(errors[USER_PROFILE_DATA.PROVIDER_ACCESS])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.PROVIDER_ACCESS]
                        ? errors[USER_PROFILE_DATA.PROVIDER_ACCESS]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {/* <div className="flex justify-end mt-10 gap-4"> */}
                {selectedUserTask?.id && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                <Button
                  //   disabled={!isValid} // Button disabled if form is invalid or submitting
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
                {/* </div> */}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
